import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import api from "../../../services/api"
import Header from "../Header"
import Sidebar from "../Menu"
import Loading from "../Loading"
import Message from "../../../components/Message"
import ModalConfigForm from "../../../components/Modal/ModalMetaPage/ModalConfigForm"
import { withMinimumLoadingDuration } from "../../../utils/minimumLoadingDuration"
import PermissionLoadError from "../../../components/PermissionLoadError"
import RenderIfUserHasPermission from "../../../components/RenderIfUserHasPermission"
import WhatsNewModal from "../../../components/WhatsNewModal"
import AnnouncementModal from "../../../components/AnnouncementModal"
import { HeaderProvider } from "../../../contexts/headerContext"
import WebSocketProvider from "../../../contexts/webSocketContext"
import WebSocketChatProvider from "../../../contexts/webSocketChatContext"
import { useData } from "../../../contexts/dataContext"
import VoipProvider from "../../../contexts/voipContext"
import { useAuth } from "../../../contexts/authContext"
import { BsMeta } from "react-icons/bs"
import { toast } from "react-toastify"
import { isValidAccountId, openLinkExternal } from "../../../utils/utils"

const dismissedLicenseMessages = new Set()

const Content = ({ title, namePermission = undefined, alternativePermissions = [], adminOnly = false, resetConfig = false, children }) => {
    const [formData, setFormData] = useState([])
    const [formConfig, setFormConfig] = useState({})
    const [questionsConfig, setQuestionsConfig] = useState([])
    const [allFields, setAllFields] = useState([])
    const [showModalConfigForms, setShowModalConfigForms] = useState(false)
    const [notifications, setNotifications] = useState([])
    const [accountExpiresIn, setAccountExpiresIn] = useState()
    const [showMessage, setShowMessage] = useState(false)
    const [pendingUpdates, setPendingUpdates] = useState([])
    const [pendingUpdateIndex, setPendingUpdateIndex] = useState(0)
    const [pendingAnnouncements, setPendingAnnouncements] = useState([])
    const [pendingAnnouncementIndex, setPendingAnnouncementIndex] = useState(0)
    const [pendingAnnouncementBanners, setPendingAnnouncementBanners] = useState([])
    const [announcementUnreadCount, setAnnouncementUnreadCount] = useState(0)
    const [bannerPreview, setBannerPreview] = useState(null)
    const [readyAccountId, setReadyAccountId] = useState()
    const [accountLoadError, setAccountLoadError] = useState()
    const [accountLoadAttempt, setAccountLoadAttempt] = useState(0)
    const [accountRetryLoading, setAccountRetryLoading] = useState(false)

    const { account_id } = useParams()
    const { setAccountId, fields, resetAllData } = useData()
    const { getAccount, getPermissions, statusAccount, permissions } = useAuth()
    const isRemoteAccess = permissions?.accessByAdmin || permissions?.accessByPartner
    const leadLimitNotification = notifications.find(notification => notification.id === "leadsLimit" && notification?.data?.reached)
    const canManagePlan = permissions?.finance >= 1 || isRemoteAccess

    const navigate = useNavigate()

    useEffect(() => {
        if (resetConfig) {
            api.defaults.headers.common['x-api-key'] = undefined
            setNotifications([])
            resetAllData()
        }
    }, [resetConfig])

    useEffect(() => {
        let active = true

        const prepareAccount = async () => {
            setReadyAccountId(undefined)
            if (!account_id) {
                setReadyAccountId(undefined)
                return
            }

            if (!isValidAccountId(account_id)) {
                navigate("/", { replace: true })
                return
            }

            api.defaults.headers.common['x-api-key'] = account_id
            const request = Promise.all([
                getAccount(account_id),
                getPermissions(account_id),
            ])
            const [loadedAccount, loadedPermissions] = await (accountLoadError === account_id
                ? withMinimumLoadingDuration(request)
                : request)

            if (!active) {
                return
            }

            setAccountRetryLoading(false)
            if (!loadedAccount || !loadedPermissions) {
                setAccountLoadError(account_id)
                return
            }

            setAccountLoadError(undefined)
            setAccountId(account_id)
            setReadyAccountId(account_id)
            setPendingUpdates([])
            setPendingUpdateIndex(0)
            setPendingAnnouncements([])
            setPendingAnnouncementIndex(0)
            setPendingAnnouncementBanners([])
            setAnnouncementUnreadCount(0)
            setBannerPreview(null)
            getAllNotifications()
            const hasPendingAnnouncements = await getLatestAnnouncements()
            if (!hasPendingAnnouncements) getLatestPlatformUpdate()
        }

        prepareAccount()

        return () => {
            active = false
        }
    }, [account_id, accountLoadAttempt])

    useEffect(() => {
        if (permissions?.finance || isRemoteAccess) {
            verifyIfAccountExpires()
        }
    }, [statusAccount, permissions?.finance, permissions?.accessByAdmin, permissions?.accessByPartner])

    useEffect(() => {
        if (fields?.length > 0) {
            setAllFields([{ id: -1, value: "Ignorar campo" }, ...fields])
        }
    }, [fields])

    useEffect(() => {
        const handleAnnouncementRead = () => setAnnouncementUnreadCount(previous => Math.max(previous - 1, 0))
        window.addEventListener("oktto:announcement-read", handleAnnouncementRead)
        return () => window.removeEventListener("oktto:announcement-read", handleAnnouncementRead)
    }, [])

    const verifyIfAccountExpires = () => {
        const daysToExpires = statusAccount?.daysToExpires
        const isExpiredRemoteAccess = isRemoteAccess && statusAccount && !statusAccount?.hasPermission
        const messageKey = `${account_id}:${isExpiredRemoteAccess ? "license-expired-support" : "license-expiration"}`

        if (dismissedLicenseMessages.has(messageKey)) {
            setShowMessage(false)
            return
        }

        if (isExpiredRemoteAccess) {
            setAccountExpiresIn(daysToExpires)
            setShowMessage(true)
        } else if (daysToExpires <= 2 && accountExpiresIn !== daysToExpires) {
            setAccountExpiresIn(daysToExpires)
            setShowMessage(true)
        } else {
            setShowMessage(false)
        }
    }

    const closeMessage = () => {
        const isExpiredRemoteAccess = isRemoteAccess && statusAccount && !statusAccount?.hasPermission
        const messageKey = `${account_id}:${isExpiredRemoteAccess ? "license-expired-support" : "license-expiration"}`
        dismissedLicenseMessages.add(messageKey)
        setShowMessage(false)
    }

    const getHeaderProviderAndChildren = () => (
        <HeaderProvider>
            <Header titleDefault={title} showOptions={!!account_id} initialNotifications={notifications} announcementUnreadCount={announcementUnreadCount} />
            <div className="content">
                {account_id &&
                    <Sidebar />
                }
                <div className="container">
                    {pendingAnnouncementBanners.map(announcement => <Message
                        key={`${announcement._id}-${announcement.revision || 1}`}
                        title={announcement.title}
                        text={announcement.summary}
                        variant={announcement.priority === "critical" ? "danger" : (announcement.priority === "important" ? "warning" : "info")}
                        actionLabel={announcement.requires_acknowledgement ? "Ler e confirmar" : "Ver comunicado"}
                        onAction={() => setBannerPreview(announcement)}
                        closeMessage={announcement.requires_acknowledgement ? undefined : () => finishBannerAnnouncement(announcement, false)}
                    />)}
                    {leadLimitNotification && <Message
                            title="Limite de leads atingido"
                            variant="danger"
                            text={canManagePlan
                                ? "A conta não pode receber novos leads. Contrate mais capacidade para restabelecer o recebimento."
                                : "A conta não pode receber novos leads. Peça a um administrador para contratar mais capacidade."}
                            actionLabel={canManagePlan ? "Contratar mais leads" : undefined}
                            onAction={canManagePlan ? () => navigate(`/${account_id}/myaccount`) : undefined}
                    />}
                    <RenderIfUserHasPermission namePermission={namePermission} alternativePermissions={alternativePermissions} adminOnly={adminOnly}>
                        {showMessage && <Message
                            closeMessage={closeMessage}
                            title={isRemoteAccess && !statusAccount?.hasPermission ? "Licença expirada" : "Lembrete de licença"}
                            variant={isRemoteAccess && !statusAccount?.hasPermission ? "danger" : "warning"}
                            text={isRemoteAccess && !statusAccount?.hasPermission ? "Os usuários da conta seguem bloqueados, mas seu acesso de suporte permanece liberado." : `Sua licença ${accountExpiresIn > 0 ? `expira em ${accountExpiresIn} ${accountExpiresIn > 1 ? "dias" : "dia"}` : (accountExpiresIn === 0 ? "expira hoje" : (accountExpiresIn === -1 ? "expirou ontem" : `expirou há ${-accountExpiresIn} dias`))}. ${accountExpiresIn >= 0 ? "Não se preocupe, basta renová-la" : "Renove agora"} para continuar desfrutando da plataforma sem interrupções!`}
                        />}
                        {children}
                    </RenderIfUserHasPermission>
                </div>
            </div>
            {showModalConfigForms &&
                <ModalConfigForm
                    icon={<BsMeta />}
                    title={formData?.name}
                    description="Configurar campos"
                    fields={allFields}
                    questions={formData?.questions}
                    formConfig={formConfig}
                    questionsConfig={questionsConfig}
                    closeModal={handleCloseModalConfigForm}
                    onSubmit={handleSaveUpdateForm}
                />
            }
            {pendingUpdates?.[pendingUpdateIndex] &&
                <WhatsNewModal
                    update={pendingUpdates[pendingUpdateIndex]}
                    onClose={markPendingPlatformUpdatesAsSeen}
                    onViewAll={handleViewAllPlatformUpdates}
                    onPrevious={showPreviousPendingUpdate}
                    onNext={showNextPendingUpdate}
                    hasPrevious={pendingUpdateIndex > 0}
                    hasNext={pendingUpdateIndex < pendingUpdates.length - 1}
                />
            }
            {pendingAnnouncements?.[pendingAnnouncementIndex] &&
                <AnnouncementModal
                    announcement={pendingAnnouncements[pendingAnnouncementIndex]}
                    onClose={() => finishCurrentAnnouncement(false)}
                    onAcknowledge={() => finishCurrentAnnouncement(true)}
                    onViewAll={viewAllAnnouncements}
                />
            }
            {bannerPreview &&
                <AnnouncementModal
                    announcement={bannerPreview}
                    onClose={() => finishBannerAnnouncement(bannerPreview, false)}
                    onAcknowledge={() => finishBannerAnnouncement(bannerPreview, true)}
                    onViewAll={viewAllFromBanner}
                />
            }
        </HeaderProvider>
    )

    const getAllNotifications = async () => {
        api.get("/accounts/notifications").then(response => {
            const notifications = response?.data?.data
            if (notifications.length > 0) {
                notifications?.forEach(notification => {
                    setNotifications(previousValue => {
                        const newValue = [...previousValue]
                        newValue.push({ id: notification.id, type: notification.type, message: notification.message, data: notification.data, onClick: () => handleClickNotification(notification.type, notification.data) })
                        return newValue
                    })
                })
            }
        }).catch(err => { })
    }

    const getLatestPlatformUpdate = async () => {
        api.get("/platform-updates/latest-unseen").then(response => {
            const updates = Array.isArray(response?.data?.data) ? response.data.data : (response?.data?.data ? [response.data.data] : [])
            setPendingUpdates(updates)
            setPendingUpdateIndex(0)
        }).catch(() => { })
    }

    const getLatestAnnouncements = async () => {
        try {
            const response = await api.get("/announcements/latest-unseen")
            const announcements = Array.isArray(response?.data?.data) ? response.data.data : []
            const modalAnnouncements = announcements.filter(announcement => !announcement.display_mode || announcement.display_mode === "modal")
            const bannerAnnouncements = announcements.filter(announcement => announcement.display_mode === "banner")
            setPendingAnnouncements(modalAnnouncements)
            setPendingAnnouncementBanners(bannerAnnouncements)
            setAnnouncementUnreadCount(announcements.length)
            setPendingAnnouncementIndex(0)
            return modalAnnouncements.length > 0
        } catch (_) {
            return false
        }
    }

    const finishCurrentAnnouncement = async (acknowledged) => {
        const current = pendingAnnouncements[pendingAnnouncementIndex]
        if (!current) return

        api.post(`/announcements/${current._id}/seen`, { acknowledged }).catch(() => { })
        setAnnouncementUnreadCount(previous => Math.max(previous - 1, 0))
        if (pendingAnnouncementIndex < pendingAnnouncements.length - 1) {
            setPendingAnnouncementIndex(previous => previous + 1)
        } else {
            setPendingAnnouncements([])
            setPendingAnnouncementIndex(0)
            getLatestPlatformUpdate()
        }
    }

    const finishBannerAnnouncement = (announcement, acknowledged) => {
        if (!announcement) return
        api.post(`/announcements/${announcement._id}/seen`, { acknowledged }).catch(() => { })
        setPendingAnnouncementBanners(previous => previous.filter(item => item._id !== announcement._id))
        setAnnouncementUnreadCount(previous => Math.max(previous - 1, 0))
        setBannerPreview(null)
    }

    const viewAllFromBanner = () => {
        if (bannerPreview && !bannerPreview.requires_acknowledgement) finishBannerAnnouncement(bannerPreview, false)
        else setBannerPreview(null)
        navigate(`/${account_id}/announcements`)
    }

    const viewAllAnnouncements = () => {
        const current = pendingAnnouncements[pendingAnnouncementIndex]
        if (current && !current.requires_acknowledgement) {
            api.post(`/announcements/${current._id}/seen`, { acknowledged: false }).catch(() => { })
            setAnnouncementUnreadCount(previous => Math.max(previous - 1, 0))
        }
        setPendingAnnouncements([])
        setPendingAnnouncementIndex(0)
        navigate(`/${account_id}/announcements`)
    }

    const markPendingPlatformUpdatesAsSeen = async () => {
        const seenUntil = new Date().toISOString()
        setPendingUpdates([])
        setPendingUpdateIndex(0)
        api.post("/platform-updates/seen", { seen_until: seenUntil }).catch(() => { })
    }

    const handleViewAllPlatformUpdates = async () => {
        await markPendingPlatformUpdatesAsSeen()
        navigate(`/${account_id}/platform-updates`)
    }

    const showPreviousPendingUpdate = () => {
        setPendingUpdateIndex(previous => Math.max(previous - 1, 0))
    }

    const showNextPendingUpdate = () => {
        setPendingUpdateIndex(previous => Math.min(previous + 1, pendingUpdates.length - 1))
    }

    const handleCloseModalConfigForm = () => {
        setFormData([])
        setShowModalConfigForms(false)
    }

    const handleSaveUpdateForm = async (data) => {
        api.patch(`/integrations/meta/accounts/${formData.page_id}/forms/${formData.id}`, data).then(response => {
            toast.success(response.data.message)
            handleCloseModalConfigForm()
        }).catch(err => {
            toast.error(err?.response?.data?.message)
        })
    }

    const handleClickNotification = (type, data) => {
        if (type === "meta_ads") {
            handleShowModalConfigForm(data.page_id, data.form_id)
        } else if (type === "task_schedule") {
            openLinkExternal(data?.link)
        } else if (type === "duplicate_queue") {
            openLinkExternal(data?.link)
        }
    }

    const handleShowModalConfigForm = (page_id, form_id) => {
        api.get(`/integrations/meta/accounts/${page_id}/forms/${form_id}/questions`).then(response => {
            const data = response?.data?.data || []
            setFormData(data.form)
            setFormConfig(data.formConfig)
            setQuestionsConfig(data.questionsConfig)
            setShowModalConfigForms(true)
        }).catch(err => {
            toast.error(err?.response?.data?.message)
        })
    }

    if (account_id && accountLoadError === account_id) {
        return <PermissionLoadError loading={accountRetryLoading} onRetry={() => {
            setAccountRetryLoading(true)
            setAccountLoadAttempt(attempt => attempt + 1)
        }} />
    }

    if (account_id && readyAccountId !== account_id) {
        return <Loading fullPage={true} />
    }

    return (
        account_id ?
            <WebSocketProvider >
                <WebSocketChatProvider>
                    <VoipProvider>
                        {getHeaderProviderAndChildren()}
                    </VoipProvider>
                </WebSocketChatProvider>
            </WebSocketProvider>
            :
            <WebSocketProvider >
                {getHeaderProviderAndChildren()}
            </WebSocketProvider>

    )
}

export default Content
